<template>
  <div>
    <v-card class="mb-4">
      <v-card-title class="d-flex align-center flex-wrap">
        <v-btn
          icon
          :ripple="false"
          title="前日"
          @click="$emit('change-date', shiftDate(-1))"
        >
          <v-icon>{{ icons.mdiChevronLeft }}</v-icon>
        </v-btn>
        <v-btn
          icon
          :ripple="false"
          title="翌日"
          @click="$emit('change-date', shiftDate(1))"
        >
          <v-icon>{{ icons.mdiChevronRight }}</v-icon>
        </v-btn>
        <span class="text-h6 me-3">{{ formatDate(date) }}</span>
        <v-btn
          small
          outlined
          :ripple="false"
          @click="$emit('change-date', todayStr)"
        >
          今日
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          small
          outlined
          :ripple="false"
          :to="floorRoute"
        >
          フロアマップへ
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-0">
        <div class="day-roster__title">
          出勤予定（{{ daySchedules.length }}名<template v-if="requiredCount != null">
            / 必要 {{ requiredCount }}名
          </template>）
          <v-btn
            v-if="canManage"
            x-small
            outlined
            :ripple="false"
            @click="$emit('open-week')"
          >
            シフトの追加・週まとめ登録は「週シフト」で ›
          </v-btn>
        </div>
        <div
          v-if="isLoading"
          class="day-roster__empty"
        >
          読み込み中...
        </div>
        <div
          v-else-if="daySchedules.length === 0"
          class="day-roster__empty"
        >
          出勤予定はありません
        </div>
        <div
          v-for="item in daySchedules"
          :key="`ds-${item.id}`"
          class="day-roster__row"
        >
          <span class="day-roster__name">{{ item.userName }}</span>
          <span
            v-if="item.startAt"
            class="day-roster__time"
            :class="{ 'day-roster__time--editable': canManage }"
            :role="canManage ? 'button' : null"
            :title="canManage ? 'クリックで時間を変更' : null"
            @click="canManage && openTimeDialog(item)"
          >{{ hhmm(item.startAt) }} 〜 {{ hhmm(item.endAt) }}</span>
          <v-chip
            v-else
            x-small
            outlined
            :ripple="false"
            :title="canManage ? 'クリックで時間を設定' : null"
            @click="canManage && openTimeDialog(item)"
          >
            時間未定
          </v-chip>
          <v-chip
            v-if="isNominationLocked(item)"
            x-small
            outlined
            color="error"
            title="指名予約があるため却下できません"
          >
            予約あり
          </v-chip>
          <span
            v-if="item.note"
            class="day-roster__note text-truncate"
            :title="item.note"
          >{{ item.note }}</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="canManage"
            x-small
            outlined
            color="error"
            :ripple="false"
            :disabled="isNominationLocked(item)"
            :loading="rejectingId === item.id"
            @click="rejectSchedule(item)"
          >
            却下
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 来店予約（予約管理と共通のボード） -->
    <v-card>
      <v-card-text>
        <div class="day-roster__title">
          来店予約
        </div>
        <reservation-day-board
          :key="`board-${date}`"
          :date="date"
          @changed="fetchDay"
        />
      </v-card-text>
    </v-card>

    <!-- シフト時刻設定 -->
    <shift-time-dialog
      v-model="isTimeDialogOpen"
      :schedule="timeTarget"
      :date="date"
      @saved="fetchDay"
    />
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { format, addDays } from 'date-fns'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import StaffingTargetApi from '@/api/waiter/StaffingTarget'
import ReservationApi from '@/api/waiter/Reservation'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import { getBusinessTodayStr } from '@/views/waiter/floorUtils'
import { resolveRequiredCount } from '@/views/waiter/shiftStaffingUtils'
import ShiftTimeDialog from '@/views/waiter/settings/ShiftTimeDialog.vue'
import ReservationDayBoard from '@/views/waiter/reservations/components/ReservationDayBoard.vue'

export default {
  components: {
    ShiftTimeDialog,
    ReservationDayBoard,
  },
  props: {
    // 対象日（yyyy-MM-dd）
    date: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { currentUser, currentClub } = useCurrentData()
    const { hhmm } = useDateFormat()

    const canManage = computed(() => {
      if (['waiter', 'admin'].includes(currentUser.value?.role)) return true

      return vm.$can('read', 'custom:settings_work_schedule_manage')
    })

    const isLoading = ref(false)
    const schedules = ref([])
    const staffingTargets = ref([])
    const reservations = ref([])

    const fetchDay = async () => {
      isLoading.value = true
      try {
        const [schedulesRes, targetsRes, reservationsRes] = await Promise.all([
          WorkScheduleApi.getWorkSchedules({
            status: 'approved', requestType: 'work', startDate: props.date, endDate: props.date,
          }),
          StaffingTargetApi.getStaffingTargets().catch(() => null),
          // 指名ロック判定用（詳細不要）
          ReservationApi.getReservations({ date: props.date }).catch(() => null),
        ])

        schedules.value = schedulesRes?.data
          ? schedulesRes.data.work_schedules.data.map(d => ({ id: d.id, ...d.attributes }))
          : []
        staffingTargets.value = targetsRes?.data
          ? targetsRes.data.staffing_targets.data.map(d => ({
            id: Number(d.id),
            dayOfWeek: d.attributes.dayOfWeek,
            date: d.attributes.date ? String(d.attributes.date).slice(0, 10) : null,
            requiredCount: d.attributes.requiredCount,
          }))
          : []
        reservations.value = reservationsRes?.data
          ? reservationsRes.data.reservations.data
            .map(d => ({ id: d.id, ...d.attributes }))
            .filter(r => r.status !== 'canceled')
          : []
      } finally {
        isLoading.value = false
      }
    }

    watch(() => props.date, fetchDay, { immediate: true })

    const daySchedules = computed(() => schedules.value
      .slice()
      .sort((a, b) => {
        if (!a.startAt && !b.startAt) return 0
        if (!a.startAt) return 1
        if (!b.startAt) return -1

        return a.startAt < b.startAt ? -1 : 1
      }))

    const requiredCount = computed(() => resolveRequiredCount(staffingTargets.value, props.date))

    const nominatedUserIds = computed(() => {
      const ids = new Set()
      reservations.value.forEach(r => (r.nominations || []).forEach(n => ids.add(Number(n.userId))))

      return ids
    })

    const isNominationLocked = item => nominatedUserIds.value.has(Number(item.userId))

    // 「今日」= 営業日（深夜帯は前日の暦日）
    const todayStr = computed(() => getBusinessTodayStr(new Date(), currentClub.value?.openAt))
    const shiftDate = diff => format(addDays(new Date(props.date), diff), 'yyyy-MM-dd')

    const formatDate = dateStr => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const dayNames = ['日', '月', '火', '水', '木', '金', '土']

      return `${date.getMonth() + 1}/${date.getDate()}（${dayNames[date.getDay()]}）`
    }

    // ── 却下 / 時刻設定 ──
    const rejectingId = ref(null)

    const rejectSchedule = async item => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`${item.userName} のシフトを却下しますか？`)) return

      rejectingId.value = item.id
      try {
        await WorkScheduleApi.rejectWorkSchedule(item.id)
        vm.$toast.success('シフトを却下しました')
        emit('changed')
        await fetchDay()
      } catch (error) {
        const message = error?.response?.data?.message
        vm.$toast.error(Array.isArray(message) ? message.join('、') : '却下に失敗しました。画面を更新して再度お試しください')
      } finally {
        rejectingId.value = null
      }
    }

    const isTimeDialogOpen = ref(false)
    const timeTarget = ref(null)

    const openTimeDialog = item => {
      timeTarget.value = item
      isTimeDialogOpen.value = true
    }

    const floorRoute = computed(() => ({
      name: 'waiter-floor',
      params: { club_slug: currentClub.value?.slug },
    }))

    return {
      canManage,
      isLoading,
      daySchedules,
      requiredCount,
      isNominationLocked,
      todayStr,
      shiftDate,
      formatDate,
      hhmm,
      fetchDay,
      rejectingId,
      rejectSchedule,
      isTimeDialogOpen,
      timeTarget,
      openTimeDialog,
      floorRoute,
      icons: {
        mdiChevronLeft,
        mdiChevronRight,
      },
    }
  },
}
</script>

<style scoped>
.day-roster__title {
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.day-roster__empty {
  font-size: 13px;
  color: #94a3b8;
  padding: 8px 0;
}

.day-roster__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.2);
}

.day-roster__name {
  font-weight: 600;
  font-size: 14px;
  min-width: 90px;
}

.day-roster__time {
  font-size: 13px;
  min-width: 110px;
}

.day-roster__time--editable {
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}

.day-roster__note {
  font-size: 12px;
  color: #94a3b8;
  max-width: 240px;
}
</style>
